import { useEffect, useTransition } from 'react';
import { Navigate, useLocation } from 'react-router';
import { AnimatePresence, motion } from 'framer-motion';

import { Button } from '@psspl/ui';

import { useAuth, type SessionEndReason, type SignInFailure } from '../auth/auth-context';
import { CompanyLogo } from '../components/brand/company-logo';
import { InlineAlert } from '../components/common/inline-alert';
import { isDevAuthEnabled } from '../dev-auth/dev-auth'; // DEV-1
import { DevLoginPanel } from '../dev-auth/dev-login-panel'; // DEV-1
import { homeRouteFor } from '../lib/access';
import { webEnv } from '../lib/env';
import { MOTION_DURATION, MOTION_EASE } from '../lib/motion';
import { safeReturnPath } from '../lib/url';
import { authService } from '../services/auth.service';

/**
 * `/login` — the only way into Prisco: "Continue with Microsoft".
 *
 * There is no email field, no password, no sign-up and no reset (ADR-24). The
 * button opens Microsoft's own hosted sign-in page in a popup; Prisco never renders
 * a Microsoft form. When the popup completes, the auth provider exchanges the token
 * and this screen forwards the user to their home area:
 * the admin area for administrators, the catalogue for everyone else. That split
 * follows the server-computed capabilities and is presentation only.
 */
export function LoginPage() {
  const { status, user, endReason, signInFailure, returnTo, signInWithMicrosoft } = useAuth();
  const location = useLocation();
  const [isSigningIn, startSignIn] = useTransition();
  const isSignedOut = status === 'unauthenticated';

  // Load MSAL while the button is on screen, so the popup opens inside the click.
  useEffect(() => {
    if (isSignedOut) {
      authService.prepareMicrosoftSignIn();
    }
  }, [isSignedOut]);

  if (status === 'authenticated' && user !== null) {
    const target = returnTo ?? safeReturnPath(fromState(location.state)) ?? homeRouteFor(user);

    return <Navigate to={target} replace />;
  }

  const isChecking = status === 'loading';
  const isBusy = isChecking || isSigningIn;

  return (
    <div className="relative flex min-h-dvh flex-col bg-surface-base">
      <div
        aria-hidden="true"
        className="psspl-page-grid pointer-events-none absolute inset-x-0 top-0 h-[32rem]"
      />
      <div
        aria-hidden="true"
        className="pointer-events-none absolute inset-x-0 top-0 h-80 bg-[radial-gradient(ellipse_60%_100%_at_50%_0%,color-mix(in_oklab,var(--color-brand-500)_14%,transparent),transparent)]"
      />

      <main className="relative flex flex-1 items-center justify-center px-4 py-12 sm:px-6">
        <motion.section
          initial={{ opacity: 0, y: 12 }}
          animate={{ opacity: 1, y: 0 }}
          transition={{ duration: 0.28, ease: MOTION_EASE }}
          aria-labelledby="sign-in-heading"
          className="w-full max-w-md"
        >
          <div className="relative overflow-hidden rounded-lg border border-line-strong bg-surface-raised px-6 py-8 shadow-dialog sm:px-10 sm:py-10">
            <span
              aria-hidden="true"
              className="absolute inset-x-0 top-0 h-px bg-gradient-to-r from-transparent via-accent-500/70 to-transparent"
            />

            <div className="flex flex-col items-center text-center">
              <CompanyLogo size="lg" />

              <p className="mt-8 text-xs font-medium uppercase tracking-widest text-accent-400">
                {webEnv.companyName}
              </p>
              <h1
                id="sign-in-heading"
                className="mt-2 text-2xl font-semibold tracking-tight text-ink-primary sm:text-3xl"
              >
                {webEnv.appName}
              </h1>
              <p className="mt-3 text-sm leading-relaxed text-ink-secondary">
                Sign in with your Microsoft account to continue.
              </p>
            </div>

            <AnimatePresence initial={false} mode="wait">
              <motion.div
                key={signInFailure ?? `end-${endReason ?? 'none'}`}
                initial={{ opacity: 0, y: -4 }}
                animate={{ opacity: 1, y: 0 }}
                exit={{ opacity: 0 }}
                transition={{ duration: MOTION_DURATION, ease: MOTION_EASE }}
                className="mt-6 flex flex-col gap-3 empty:hidden"
              >
                {signInFailure === null ? (
                  <SessionEndNotice reason={endReason} />
                ) : (
                  <SignInFailureNotice failure={signInFailure} />
                )}
              </motion.div>
            </AnimatePresence>

            <Button
              size="lg"
              variant="secondary"
              className="mt-6 w-full"
              isLoading={isBusy}
              leadingIcon={<MicrosoftLogo />}
              onClick={() => {
                startSignIn(async () => {
                  await signInWithMicrosoft(safeReturnPath(fromState(location.state)));
                });
              }}
            >
              {isChecking
                ? 'Checking your session'
                : isSigningIn
                  ? 'Waiting for Microsoft'
                  : 'Continue with Microsoft'}
            </Button>

            {/* DEV-1: development-only mock sign-in. Removed from production builds. */}
            {import.meta.env.DEV && isDevAuthEnabled() && !isChecking ? <DevLoginPanel /> : null}

            <p className="mt-6 text-center text-xs leading-relaxed text-ink-muted">
              Access is restricted to authorized Prisco users.
            </p>
          </div>
        </motion.section>
      </main>

      <footer className="relative px-4 pb-6 text-center text-xs text-ink-muted">
        © {new Date().getFullYear()} {webEnv.companyName}
      </footer>
    </div>
  );
}

/** `location.state.from`, set by `RequireAuth` when it bounced a deep link here. */
function fromState(state: unknown): unknown {
  return typeof state === 'object' && state !== null && 'from' in state
    ? (state as { readonly from: unknown }).from
    : null;
}

function SessionEndNotice({ reason }: { readonly reason: SessionEndReason | null }) {
  switch (reason) {
    case 'expired':
      return (
        <InlineAlert tone="warning">Your session has expired. Please sign in again.</InlineAlert>
      );
    case 'signed-out':
      return <InlineAlert tone="info">You have signed out of Prisco.</InlineAlert>;
    case 'signed-out-unconfirmed':
      return (
        <InlineAlert tone="warning" title="You have been signed out on this device">
          We could not confirm the sign-out with the server, so the session may stay valid until it
          expires. If this is a shared computer, close the browser.
        </InlineAlert>
      );
    case null:
      return null;
  }
}

/**
 * One message per failure kind. None repeats a server message or names the check
 * that failed; only the two account states the API chooses to disclose are
 * explained (ADR-29).
 */
function SignInFailureNotice({ failure }: { readonly failure: SignInFailure }) {
  switch (failure) {
    case 'not-provisioned':
      return (
        <InlineAlert tone="danger" title="Account not authorized">
          Your Microsoft account is not authorized to access Prisco. Please contact your Prisco
          administrator.
        </InlineAlert>
      );
    case 'inactive':
      return (
        <InlineAlert tone="danger" title="Account inactive">
          Your Prisco account has been deactivated. Please contact your Prisco administrator if you
          need access again.
        </InlineAlert>
      );
    case 'cancelled':
      return (
        <InlineAlert tone="info">
          Microsoft sign-in was cancelled. Continue with Microsoft when you are ready.
        </InlineAlert>
      );
    case 'popup-blocked':
      return (
        <InlineAlert tone="warning" title="Sign-in window was blocked">
          Your browser blocked the Microsoft sign-in window. Allow pop-ups for this site, then
          continue with Microsoft again.
        </InlineAlert>
      );
    case 'microsoft-failed':
      return (
        <InlineAlert tone="danger" title="Microsoft sign-in did not complete">
          Something went wrong while signing in with Microsoft. Please try again.
        </InlineAlert>
      );
    case 'rate-limited':
      return (
        <InlineAlert tone="warning" title="Too many sign-in attempts">
          For your security, sign-in is paused for a moment. Wait a minute, then try again.
        </InlineAlert>
      );
    case 'unavailable':
      return (
        <InlineAlert tone="danger" title="Prisco is not reachable">
          We could not reach Prisco to complete sign-in. Check your connection, then try again.
        </InlineAlert>
      );
    case 'not-configured':
    case 'endpoint-missing':
      return (
        <InlineAlert tone="danger" title="Sign-in is unavailable">
          Microsoft sign-in is not set up for this Prisco environment yet. Please contact your
          Prisco administrator.
        </InlineAlert>
      );
    case 'rejected':
      return (
        <InlineAlert tone="danger" title="We couldn’t sign you in">
          Prisco could not verify your Microsoft sign-in. Make sure you use your work account, then
          try again. If the problem continues, contact your Prisco administrator.
        </InlineAlert>
      );
  }
}

/** Microsoft's four-square mark, as its sign-in branding guidelines specify. */
function MicrosoftLogo() {
  return (
    <svg viewBox="0 0 21 21" className="size-5 shrink-0" aria-hidden="true">
      <rect x="1" y="1" width="9" height="9" fill="#f25022" />
      <rect x="11" y="1" width="9" height="9" fill="#7fba00" />
      <rect x="1" y="11" width="9" height="9" fill="#00a4ef" />
      <rect x="11" y="11" width="9" height="9" fill="#ffb900" />
    </svg>
  );
}
